import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, AfterViewInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { takeUntil, catchError, of, switchMap } from 'rxjs';
import { DestroyService, AlertService } from '@selfai-platform/shared';
import { Button } from 'primeng/button';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { DragDropData, DragDropService, ChatService, SchemaExtractionService } from '../../services';
import { Agent } from '../../models';
import { MessageSentEvent, ErrorEvent, ContextClearedEvent } from '../../types';
import { PrimeTemplate } from 'primeng/api';
@Component({
  selector: 'selfai-platform-chat-message-input',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    Button,
    InputTextareaModule,
    PrimeTemplate,
  ],
  templateUrl: './chat-message-input.component.html',
  styleUrls: ['./chat-message-input.component.scss'],
  providers: [DestroyService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatMessageInputComponent implements AfterViewInit, OnDestroy {
  @Input() selectedAgent: Agent | null = null;
  @Input() isProcessing = false;
  @Input() contextData = '';
  @Output() messageSent = new EventEmitter<MessageSentEvent>();
  @Output() contextCleared = new EventEmitter<ContextClearedEvent>();
  @Output() errorOccurred = new EventEmitter<ErrorEvent>();

  @ViewChild('messageInputElement') private messageInputRef!: ElementRef;

  public messageInput = '';
  public jsonDataInput = '';
  public jsonError = '';
  public hasValidContext = false;
  public showContextTip = true;

  private static readonly CONTEXT_TIP_DISMISSED_KEY = 'chat-context-tip-dismissed';

  constructor(
    private readonly dragDropService: DragDropService,
    private readonly chatService: ChatService,
    private readonly schemaExtractionService: SchemaExtractionService,
    private readonly destroy$: DestroyService,
    private readonly cdr: ChangeDetectorRef,
    private readonly translateService: TranslateService,
    private readonly alertService: AlertService
  ) {}

  public ngAfterViewInit(): void {
    this.setupDropZone();
    this.jsonDataInput = this.contextData;
    this.updateContextValidation();

    const isDismissed = localStorage.getItem(ChatMessageInputComponent.CONTEXT_TIP_DISMISSED_KEY) === 'true';
    this.showContextTip = !isDismissed;
    this.cdr.markForCheck();
  }

  public ngOnDestroy(): void {
    if (this.messageInputRef?.nativeElement) {
      this.dragDropService.unregisterDropZone(this.messageInputRef.nativeElement);
    }
  }

  public sendMessage(): void {
    if (!this.messageInput.trim() || !this.selectedAgent) return;

    let jsonData = undefined;
    if (this.jsonDataInput.trim()) {
      try {
        jsonData = JSON.parse(this.jsonDataInput);
        this.jsonError = '';
      } catch (error) {
        this.jsonError = this.translateService.instant('chat.errors.invalid-json-format');
        return;
      }
    }

    this.messageSent.emit({
      message: this.messageInput,
      jsonData: jsonData as Record<string, unknown>,
    });

    this.messageInput = '';
  }

  public handleEnterKey(event: Event): void {
    const keyboardEvent = event as KeyboardEvent;
    if (!keyboardEvent.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  public getNodeName(): string {
    try {
      return JSON.parse(this.jsonDataInput || '{}')?.operation?.name || '';
    } catch {
      return '';
    }
  }

  public dismissContextTip(): void {
    this.showContextTip = false;
    localStorage.setItem(ChatMessageInputComponent.CONTEXT_TIP_DISMISSED_KEY, 'true');
    this.cdr.markForCheck();
  }

  private setupDropZone(): void {
    if (this.messageInputRef?.nativeElement) {
      const messageInput = this.messageInputRef.nativeElement;

      this.dragDropService.registerDropZone(messageInput, (nodeData) => {
        this.handleNodeDrop(nodeData);
      });
    } else {
      setTimeout(() => {
        if (this.messageInputRef?.nativeElement) {
          this.dragDropService.registerDropZone(this.messageInputRef.nativeElement, (nodeData) => {
            this.handleNodeDrop(nodeData);
          });
        }
      }, 200);
    }
  }

  private handleNodeDrop(nodeData: DragDropData): void {
    const workflowId = nodeData.serializedData?.workflowId;
    const nodeId = nodeData.serializedData?.id;

    if (workflowId && nodeId) {
      const precedingNodeId = this.findPrecedingNodeId(nodeData.serializedData);

      if (precedingNodeId) {
        this.schemaExtractionService
          .extractSchemaFromNode(workflowId, precedingNodeId)
          .pipe(
            switchMap((schemaResult) => {
              const enhancedContext = {
                ...nodeData.serializedData,
                precedingNodeSchema: schemaResult.success ? schemaResult.schema : null,
                precedingNodeId: precedingNodeId,
                schemaSource: schemaResult.success ? 'backend' : 'none',
                schemaError: schemaResult.success ? null : schemaResult.error,
              };

              return this.chatService.setContext(JSON.stringify(enhancedContext));
            }),
            catchError((error) => {
              this.alertService.error(this.translateService.instant('chat.errors.error-setting-enhanced-context') + ':', error);
              const fallbackContext = {
                ...nodeData.serializedData,
                precedingNodeSchema: null,
                precedingNodeId: precedingNodeId,
                schemaSource: 'error',
                schemaError: error.message,
              };
              return this.chatService.setContext(JSON.stringify(fallbackContext));
            }),
            takeUntil(this.destroy$),
          )
          .subscribe(() => {
            this.jsonDataInput = JSON.stringify(nodeData.serializedData, null, 2);
            this.jsonError = '';
            this.updateContextValidation();
            this.cdr.markForCheck();
          });
      } else {
        this.chatService
          .setContext(JSON.stringify(nodeData.serializedData))
          .pipe(
            catchError((error) => {
              this.alertService.error(this.translateService.instant('chat.errors.error-setting-context') + ':', error);
              return of(null);
            }),
            takeUntil(this.destroy$),
          )
          .subscribe(() => {
            this.jsonDataInput = JSON.stringify(nodeData.serializedData, null, 2);
            this.jsonError = '';
            this.updateContextValidation();
            this.cdr.markForCheck();
          });
      }
    } else {
      this.chatService
        .setContext(JSON.stringify(nodeData.serializedData))
        .pipe(
          catchError((error) => {
            this.alertService.error(this.translateService.instant('chat.errors.error-setting-context') + ':', error);
            return of(null);
          }),
          takeUntil(this.destroy$),
        )
        .subscribe(() => {
          this.jsonDataInput = JSON.stringify(nodeData.serializedData, null, 2);
          this.jsonError = '';
          this.updateContextValidation();
        });
    }
  }

  private findPrecedingNodeId(nodeData: any): string | null {
    try {
      const workflowService = (window as any).angularWorkflowService;

      if (workflowService) {
        const currentWorkflow = workflowService.getCurrentWorkflow();
        const currentNode = currentWorkflow.getNodeById(nodeData.id);

        if (currentNode && currentNode.edges && currentNode.edges.length > 0) {
          const inputEdges = currentNode.edges.filter((edge: any) => edge.endNodeId === nodeData.id);

          if (inputEdges.length > 0) {
            const firstInputEdge = inputEdges[0];
            return firstInputEdge.startNodeId;
          }
        }
      }

      if (nodeData.inputConnections && nodeData.inputConnections.length > 0) {
        const firstInputConnection = nodeData.inputConnections[0];
        if (firstInputConnection && firstInputConnection.nodeId) {
          return firstInputConnection.nodeId;
        }
      }

      if (nodeData.input && nodeData.input.length > 0) {
        const firstInput = nodeData.input[0];
        if (firstInput && firstInput.connectedFrom) {
          return firstInput.connectedFrom.nodeId || firstInput.connectedFrom.id;
        }
      }

      if (nodeData.edges && nodeData.edges.input && nodeData.edges.input.length > 0) {
        const firstEdge = nodeData.edges.input[0];
        if (firstEdge && firstEdge.from) {
          return firstEdge.from.nodeId || firstEdge.from.id;
        }
      }

      return null;
    } catch (error) {
      this.alertService.error(this.translateService.instant('chat.errors.error-finding-preceding-node') + ':', error);
      return null;
    }
  }

  private updateContextValidation(): void {
    this.hasValidContext = true;
  }
}
